var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo, useCallback, useRef } from 'react';
import { faTrashAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import PolicyFormInput from './PolicyFormInput';
import { formatDate } from '../../utils/common';
var MultiSelectTable = function (_a) {
    var _b;
    var formik = _a.formik, formikValues = _a.formikValues, question = _a.question, apiValidationErrors = _a.apiValidationErrors, targetQuestionKey = _a.targetQuestionKey, answers = _a.answers, groupKey = _a.groupKey;
    var columns = question.questions || [];
    var options = ((_b = columns[0]) === null || _b === void 0 ? void 0 : _b.options) || [];
    // Ref for scrolling
    var scrollContainerRef = useRef(null);
    var gridTemplate = useMemo(function () {
        if (!columns || columns.length === 0)
            return '3fr 1fr';
        var dynamicCols = columns.map(function () { return 'minmax(200px, 1fr)'; }).join(' ');
        var lastCol = 'minmax(60px, 80px)';
        return "".concat(dynamicCols, " ").concat(lastCol);
    }, [columns]);
    // Get all target values (rows) from formik
    var allTargetValues = useMemo(function () {
        var targetAllKeys = Object.keys(formikValues).filter(function (k) {
            return new RegExp("^".concat(question.targetQuestionKey, "__[0-9]+$")).test(k);
        });
        var targetValues = targetAllKeys.map(function (k) {
            return {
                key: k,
                value: formikValues[k],
                index: parseInt(k.split('__')[1])
            };
        });
        return targetValues;
    }, [formikValues, question.targetQuestionKey]);
    var availableOptions = useMemo(function () {
        return options.filter(function (opt) { return !allTargetValues.some(function (r) { return r.value === opt.caption; }); });
    }, [options, allTargetValues]);
    var getOptionsForRow = useCallback(function (rowValue) {
        var currentOption = rowValue && !availableOptions.some(function (o) { return o.caption === rowValue; })
            ? [{ value: rowValue, caption: rowValue }]
            : [];
        return __spreadArray(__spreadArray([], currentOption, true), availableOptions, true);
    }, [availableOptions]);
    var canDeleteRow = useMemo(function () {
        return allTargetValues.length > 1;
    }, [allTargetValues.length]);
    var scrollToBottom = useCallback(function () {
        setTimeout(function () {
            var container = scrollContainerRef.current;
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 100);
    }, []);
    var handleAddRow = useCallback(function () {
        var _a;
        if (availableOptions.length === 0 ||
            allTargetValues.length > options.length - 1) {
            return;
        }
        var nextIndex = allTargetValues.length > 0
            ? Math.max.apply(Math, allTargetValues.map(function (r) { return r.index; })) + 1
            : 0;
        var newTargetKey = "".concat(question.targetQuestionKey, "__").concat(nextIndex);
        formik.setValues(__assign(__assign({}, formik.values), (_a = {}, _a[newTargetKey] = '', _a)));
        // Scroll to the new row
        scrollToBottom();
    }, [
        formik,
        question.targetQuestionKey,
        availableOptions.length,
        allTargetValues,
        options.length,
        scrollToBottom,
    ]);
    var handleDeleteRow = useCallback(function (row) {
        if (!canDeleteRow) {
            return;
        }
        var relatedDataKey = "".concat(question.questionKey, "_").concat(row.value);
        var _a = formik.values, _b = row.key, _removedTarget = _a[_b], _c = relatedDataKey, _removedData = _a[_c], remainingValues = __rest(_a, [typeof _b === "symbol" ? _b : _b + "", typeof _c === "symbol" ? _c : _c + ""]);
        formik.setValues(remainingValues);
        var newErrors = __assign({}, formik.errors);
        delete newErrors[row.key];
        delete newErrors[relatedDataKey];
        formik.setErrors(newErrors);
    }, [canDeleteRow, question.questionKey, formik]);
    var handleRowValueChange = useCallback(function (oldValue, newValue, rowKey, rowIndex) {
        var _a;
        // Keys for the old and new data objects
        var oldDataKey = "".concat(question.questionKey, "_").concat(oldValue);
        var newDataKey = "".concat(question.questionKey, "_").concat(newValue);
        // Get the existing data for this row
        var existingData = formik.values[oldDataKey] || {};
        // Update formik values
        var updatedValues = __assign(__assign({}, formik.values), (_a = {}, _a[rowKey] = newValue, _a[newDataKey] = existingData, _a));
        // Remove old data key if it's different
        if (oldDataKey !== newDataKey) {
            delete updatedValues[oldDataKey];
        }
        formik.setValues(updatedValues);
    }, [question.questionKey, formik]);
    return (React.createElement("div", { className: "py-8 w-full" },
        React.createElement("div", { className: "mx-auto animate-fade-in" },
            React.createElement("div", { className: "overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0" },
                React.createElement("div", { className: "inline-block min-w-full align-middle" },
                    React.createElement("div", { className: "bg-[#F8FAFE] rounded-2xl mb-2" },
                        React.createElement("div", { className: "grid gap-4 px-6 py-4 text-left items-center", style: { gridTemplateColumns: gridTemplate } }, columns.map(function (col, index) { return (React.createElement("div", { key: "header_".concat(col.questionKey), className: "font-semibold text-slate-700 uppercase text-sm ".concat(index < columns.length - 1
                                ? 'border-r border-[#adb1b9] pr-4'
                                : '') }, col.question)); }))),
                    React.createElement("div", { ref: scrollContainerRef, className: "max-h-[400px] overflow-y-auto scroll-smooth" },
                        React.createElement("div", { className: "divide-y divide-slate-100" }, allTargetValues.map(function (row, i) {
                            var _a;
                            var rowValue = row.value;
                            var isRowDisabled = !rowValue;
                            return (React.createElement("div", { key: "row__".concat(i, "__").concat(question.questionKey), className: "grid gap-4 px-6 py-2 items-center justify-center", style: { gridTemplateColumns: gridTemplate } }, (_a = question.questions) === null || _a === void 0 ? void 0 :
                                _a.map(function (innerQues, colIndex) {
                                    var _a, _b, _c, _d, _e, _f;
                                    var questionKey = innerQues.questionKey;
                                    var value = '';
                                    var isDisabled = false;
                                    var errorObj = undefined;
                                    var dataKey = '';
                                    // Handle the target question (dropdown)
                                    if (questionKey === targetQuestionKey) {
                                        questionKey = "".concat(targetQuestionKey, "__").concat(row.index);
                                        errorObj =
                                            apiValidationErrors || formik.submitCount > 0
                                                ? (_a = formik.errors) === null || _a === void 0 ? void 0 : _a[questionKey]
                                                : undefined;
                                        value = rowValue;
                                    }
                                    else {
                                        // Handle other questions in the row
                                        dataKey = isRowDisabled
                                            ? "".concat(question.questionKey, "_disabled")
                                            : "".concat(question.questionKey, "_").concat(rowValue);
                                        if (isRowDisabled) {
                                            isDisabled = true;
                                        }
                                        errorObj =
                                            apiValidationErrors || formik.submitCount > 0
                                                ? (_c = (_b = formik.errors) === null || _b === void 0 ? void 0 : _b[dataKey]) === null || _c === void 0 ? void 0 : _c[innerQues.questionKey]
                                                : undefined;
                                        questionKey = "".concat(dataKey, ".").concat(questionKey);
                                        value = isDisabled
                                            ? null
                                            : _.get(formikValues, questionKey);
                                    }
                                    return (React.createElement("div", { key: questionKey, className: colIndex < (((_d = question.questions) === null || _d === void 0 ? void 0 : _d.length) || 0) - 1
                                            ? 'border-r border-[#F4F7FE] pr-4'
                                            : '' },
                                        React.createElement(PolicyFormInput, { questionKey: questionKey, question: innerQues, dataField: questionKey, label: '', value: value, voidValue: innerQues.value, inputType: innerQues.inputType, min: innerQues.min, max: innerQues.max, validation: innerQues.validation, isDisabled: isDisabled, helper: innerQues.helper, hint: innerQues === null || innerQues === void 0 ? void 0 : innerQues.hint, handleChange: questionKey ===
                                                "".concat(targetQuestionKey, "__").concat(row.index)
                                                ? function (e) {
                                                    var newValue = e.target.value;
                                                    handleRowValueChange(rowValue, newValue, questionKey, row.index);
                                                }
                                                : formik === null || formik === void 0 ? void 0 : formik.handleChange(questionKey), handleChangeDate: function (date) {
                                                var dateObj = '';
                                                if (date) {
                                                    dateObj = formatDate(new Date(date));
                                                }
                                                formik.setFieldValue(questionKey, dateObj);
                                            }, handleChangeAutocomplete: function (val) {
                                                formik.setFieldValue(questionKey, val, true);
                                            }, handleBlur: formik.handleBlur, options: questionKey ===
                                                "".concat(targetQuestionKey, "__").concat(row.index)
                                                ? getOptionsForRow(rowValue)
                                                : innerQues.options, placeholder: innerQues.placeholder, defaultChecked: ((_f = (_e = formik === null || formik === void 0 ? void 0 : formik.values) === null || _e === void 0 ? void 0 : _e[dataKey]) === null || _f === void 0 ? void 0 : _f[innerQues === null || innerQues === void 0 ? void 0 : innerQues.questionKey]) || null, errors: errorObj, touched: apiValidationErrors || formik.submitCount > 0, docLink: innerQues.docLink, linkText: innerQues.linkText, allowMultiple: innerQues.allowMultiple, enforceNone: innerQues.enforceNone, subHeading: innerQues === null || innerQues === void 0 ? void 0 : innerQues.subHeading, variant: innerQues === null || innerQues === void 0 ? void 0 : innerQues.variant })));
                                }),
                                React.createElement("div", { className: "flex justify-end items-center" },
                                    React.createElement("button", { type: "button", onClick: function () { return handleDeleteRow(row); }, disabled: !canDeleteRow, className: "p-2 rounded-lg transition-colors flex-shrink-0 ".concat(canDeleteRow
                                            ? 'hover:bg-red-50 cursor-pointer'
                                            : 'opacity-30 cursor-not-allowed'), "aria-label": "Delete ".concat(row.key), title: !canDeleteRow
                                            ? 'At least one row is required'
                                            : 'Delete row' },
                                        React.createElement(FontAwesomeIcon, { icon: faTrashAlt, className: "w-4 h-4 text-[#7e8084]" })))));
                        }))))),
            React.createElement("div", { className: "border-t border-slate-200 px-6 py-4 mt-4" },
                React.createElement("button", { type: "button", onClick: handleAddRow, disabled: availableOptions.length === 0 ||
                        allTargetValues.length > options.length - 1, className: "flex items-center gap-2 px-4 py-2 bg-transparent text-primary rounded-lg transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed" },
                    React.createElement(FontAwesomeIcon, { icon: faPlus, className: "w-4 h-4" }),
                    React.createElement("span", { className: "text-sm font-medium" }, availableOptions.length === 0 ||
                        allTargetValues.length > options.length - 1
                        ? 'No more options available'
                        : 'Add another'))))));
};
export default MultiSelectTable;
